import React, { useState, useMemo, useEffect, useRef } from 'react';
import Select from 'react-select';
import Modal from '../common/Modal';
import { fieldStyles } from '../../config/formStyles';
import { formOptions } from '../../constants/formOptions';
import {
  formatTasaForDisplay,
  formatNumberForDisplay,
  formatNumberForInput,
  parseNumberForState,
  parseVnForState,
  parseTasaForState,
  formatComitenteForDisplay,
  formatCuitForDisplay,
  parseCuitDigits,
} from '../../utils/formatters';
import { createOrden, updateOrden } from '../../services/licitacionesService';
import { saveDatosFiscalesCliente } from '../../services/clientesService';
import { useNombresComerciales } from '../../hooks/useNombresComerciales';
import { comercialPorDefecto } from '../../utils/rolesUsuario';
import { colocadoresDeLicitacion, resolverColocadorOrden } from '../../utils/colocadores';

const initialFormState = {
  especieId: '',
  // Colocador por el que se coloca la orden (licitaciones con colocadores).
  colocadorId: '',
  nroComitente: '',
  cliente: '',
  cuit: '',
  tipoDocumento: 'CUIT',
  comercial: '',
  tramo: 'Competitivo',
  monedaIntegracion: '',
  obs: '',
};

/**
 * Modal de alta/edición de órdenes de una licitación.
 * - La especie define si se oferta tasa o precio; el tramo No Competitivo
 *   no lleva ninguno de los dos.
 * - "Guardar y duplicar" persiste la orden y re-hidrata el formulario
 *   conservando especie/tramo/oferta para carga en ráfaga de clientes.
 * - duplicateFrom: orden existente usada como plantilla para una nueva
 *   (hidrata todo menos comitente/cliente/cuit).
 * - El CUIT y el tipo de documento se autocompletan desde el padrón de clientes y
 *   se guardan ahí al confirmar la orden, así la próxima licitación ya los trae
 *   cargados. Son obligatorios para el Excel de carga al sistema.
 * - Si la licitación tiene colocadores, la orden dice por cuál se coloca
 *   (obligatorio; con uno solo queda elegido). La lista sale de la licitación, no
 *   del catálogo: los productores ven los nombres pero no los fees.
 */
const LicitacionOrdenFormModal = ({
  isOpen,
  onClose,
  onSaved,
  licitacion,
  editingOrden = null,
  duplicateFrom = null,
  clientesDb,
  datosFiscalesDb,
  cargaCerrada = false,
  userRole,
  userName,
  userId,
}) => {
  const [form, setForm] = useState(initialFormState);
  const [tasaInputValue, setTasaInputValue] = useState('');
  const [precioInputValue, setPrecioInputValue] = useState('');
  const [vnInputValue, setVnInputValue] = useState('');
  const [formError, setFormError] = useState('');
  const [savedCount, setSavedCount] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const didInit = useRef(false);

  // Padrón de comerciales: usuarios que ingresaron a la app con el rol
  // Comercial (colección poblada desde la selección de rol, sin hardcodeo).
  // Un Comercial (o un productor, que opera con ese rol) no elige comercial: el
  // select queda fijo en su nombre, así que no hace falta leer el padrón (y las
  // reglas no se lo permiten al productor).
  const comerciales = useNombresComerciales(isOpen && userRole !== 'Comercial');

  const especies = useMemo(() => licitacion?.especies || [], [licitacion]);
  const colocadores = useMemo(() => colocadoresDeLicitacion(licitacion), [licitacion]);
  const conColocadores = colocadores.length > 0;
  const especieSeleccionada = useMemo(
    () => especies.find(e => e.id === form.especieId) || null,
    [especies, form.especieId]
  );

  const comitenteOptions = useMemo(() =>
    clientesDb ? Object.keys(clientesDb).map(c => ({ value: c, label: formatComitenteForDisplay(c) })) : [],
    [clientesDb]
  );

  const clienteOptions = useMemo(() =>
    clientesDb ? Object.entries(clientesDb).map(([value, label]) => ({ value, label: `${label} - ${formatComitenteForDisplay(value)}` })) : [],
    [clientesDb]
  );

  const commercialOptions = useMemo(() => {
    const options = new Set(comerciales);
    // El valor ya guardado en la orden (edición/duplicado) siempre debe ser
    // seleccionable, aunque ese comercial todavía no figure en el padrón.
    if (form.comercial) options.add(form.comercial);
    return [...options].sort((a, b) => a.localeCompare(b, 'es'));
  }, [comerciales, form.comercial]);

  useEffect(() => {
    if (!isOpen) { didInit.current = false; return; }
    if (didInit.current) return;
    didInit.current = true;
    setFormError('');
    setSavedCount(0);
    setIsSaving(false);

    const colocadorDe = (orden) => resolverColocadorOrden(orden, colocadores)?.id || '';

    if (editingOrden) {
      setForm({
        especieId: editingOrden.especieId,
        colocadorId: colocadorDe(editingOrden),
        nroComitente: editingOrden.nroComitente,
        cliente: editingOrden.cliente,
        cuit: editingOrden.cuit || '',
        tipoDocumento: editingOrden.tipoDocumento || 'CUIT',
        comercial: editingOrden.comercial,
        tramo: editingOrden.tramo,
        monedaIntegracion: editingOrden.monedaIntegracion,
        obs: editingOrden.obs || '',
      });
      setTasaInputValue(editingOrden.tasaOfertada ? formatTasaForDisplay(editingOrden.tasaOfertada) : '');
      setPrecioInputValue(editingOrden.precioOfertado ? formatNumberForDisplay(editingOrden.precioOfertado) : '');
      setVnInputValue(formatNumberForDisplay(editingOrden.cantidadVN, 0));
      return;
    }

    if (duplicateFrom) {
      setForm({
        especieId: duplicateFrom.especieId,
        colocadorId: colocadorDe(duplicateFrom),
        nroComitente: '',
        cliente: '',
        cuit: '',
        tipoDocumento: 'CUIT',
        comercial: userRole === 'Comercial' ? userName : duplicateFrom.comercial,
        tramo: duplicateFrom.tramo,
        monedaIntegracion: duplicateFrom.monedaIntegracion,
        obs: duplicateFrom.obs || '',
      });
      setTasaInputValue(duplicateFrom.tasaOfertada ? formatTasaForDisplay(duplicateFrom.tasaOfertada) : '');
      setPrecioInputValue(duplicateFrom.precioOfertado ? formatNumberForDisplay(duplicateFrom.precioOfertado) : '');
      setVnInputValue(formatNumberForDisplay(duplicateFrom.cantidadVN, 0));
      return;
    }

    const defaultEspecie = especies.length === 1 ? especies[0] : null;
    setForm({
      ...initialFormState,
      especieId: defaultEspecie?.id || '',
      colocadorId: colocadores.length === 1 ? colocadores[0].id : '',
      monedaIntegracion: defaultEspecie?.monedasIntegracion?.[0] || '',
      comercial: comercialPorDefecto({ userRole, userName }),
    });
    setTasaInputValue('');
    setPrecioInputValue('');
    setVnInputValue('');
  }, [isOpen, editingOrden, duplicateFrom, especies, colocadores, userRole, userName]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleEspecieChange = (e) => {
    const especieId = e.target.value;
    const especie = especies.find(esp => esp.id === especieId);
    setForm(prev => ({
      ...prev,
      especieId,
      monedaIntegracion: especie?.monedasIntegracion?.includes(prev.monedaIntegracion)
        ? prev.monedaIntegracion
        : (especie?.monedasIntegracion?.[0] || ''),
    }));
  };

  // Al elegir el comitente se traen sus datos fiscales del padrón. Si no tiene
  // nada guardado se limpian, para no arrastrar los del cliente anterior en la
  // carga en ráfaga.
  const handleComitenteChange = (selectedOption) => {
    const nroComitente = selectedOption ? selectedOption.value : '';
    const cliente = nroComitente && clientesDb ? clientesDb[nroComitente] : '';
    const fiscales = datosFiscalesDb?.[nroComitente];
    setForm(prev => ({
      ...prev,
      nroComitente,
      cliente,
      cuit: fiscales?.cuit || '',
      tipoDocumento: fiscales?.tipoDocumento || 'CUIT',
    }));
  };

  const buildPayload = () => ({
    licitacionId: licitacion.id,
    ...(conColocadores ? { colocadorId: form.colocadorId } : {}),
    especieId: form.especieId,
    nroComitente: form.nroComitente,
    cliente: form.cliente,
    cuit: formatCuitForDisplay(form.cuit),
    tipoDocumento: form.tipoDocumento,
    comercial: userRole === 'Comercial' ? userName : form.comercial,
    tramo: form.tramo,
    tasaOfertada: form.tramo === 'No Competitivo' || especieSeleccionada?.modalidad !== 'Tasa'
      ? 0 : (parseFloat(parseTasaForState(tasaInputValue)) || 0),
    precioOfertado: form.tramo === 'No Competitivo' || especieSeleccionada?.modalidad !== 'Precio'
      ? 0 : (parseFloat(parseNumberForState(precioInputValue)) || 0),
    cantidadVN: parseFloat(parseVnForState(vnInputValue)) || 0,
    monedaIntegracion: form.monedaIntegracion,
    obs: form.obs,
  });

  const validate = () => {
    // Red de seguridad: el tab ya oculta los accesos, pero la carga puede
    // cerrarse por fecha mientras el modal está abierto.
    if (cargaCerrada) return 'La carga de órdenes de esta licitación está cerrada.';
    if (!form.especieId) return 'Seleccioná la especie.';
    if (conColocadores && !colocadores.some(c => c.id === form.colocadorId)) {
      return 'Elegí el colocador por el que se coloca la orden.';
    }
    if (!clientesDb?.[form.nroComitente]) return 'El Nro de Comitente es inválido o no existe.';
    const cuitDigits = parseCuitDigits(form.cuit);
    if (cuitDigits && cuitDigits.length !== 11) return 'El CUIT debe tener 11 dígitos.';
    if (!form.comercial && userRole !== 'Comercial') return 'Seleccioná el comercial.';
    if (!form.monedaIntegracion) return 'Seleccioná la moneda de integración.';
    const vn = parseFloat(parseVnForState(vnInputValue)) || 0;
    if (vn <= 0) return 'Ingresá la cantidad (VN) a licitar.';
    if (form.tramo === 'Competitivo') {
      if (especieSeleccionada?.modalidad === 'Tasa' && !(parseFloat(parseTasaForState(tasaInputValue)) > 0)) {
        return 'El tramo competitivo requiere la tasa ofertada.';
      }
      if (especieSeleccionada?.modalidad === 'Precio' && !(parseFloat(parseNumberForState(precioInputValue)) > 0)) {
        return 'El tramo competitivo requiere el precio ofertado.';
      }
    }
    return '';
  };

  /**
   * Deja los datos fiscales en el padrón de clientes para la próxima carga. No
   * bloquea el guardado de la orden: si falla, la orden ya quedó bien y solo se
   * pierde el autocompletado.
   */
  const persistDatosFiscalesEnPadron = async (nroComitente, { cuit, tipoDocumento }) => {
    if (!nroComitente) return;
    const guardado = datosFiscalesDb?.[nroComitente];
    const cambio = {};
    if (cuit && guardado?.cuit !== cuit) cambio.cuit = cuit;
    if (tipoDocumento && guardado?.tipoDocumento !== tipoDocumento) cambio.tipoDocumento = tipoDocumento;
    if (Object.keys(cambio).length === 0) return;
    try {
      await saveDatosFiscalesCliente(nroComitente, cambio);
    } catch (error) {
      console.error('No se pudieron guardar los datos fiscales en el padrón de clientes:', error);
    }
  };

  const save = async () => {
    const error = validate();
    if (error) { setFormError(error); return false; }
    setFormError('');
    setIsSaving(true);
    try {
      const payload = buildPayload();
      if (editingOrden) {
        await updateOrden(editingOrden.id, payload);
      } else {
        await createOrden(payload, { userId, userName });
      }
      await persistDatosFiscalesEnPadron(payload.nroComitente, payload);
      onSaved?.();
      return true;
    } catch (err) {
      console.error('Error guardando la orden:', err);
      setFormError('No se pudo guardar la orden. Intentá de nuevo.');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ok = await save();
    if (ok) onClose?.();
  };

  // Guarda y deja el formulario listo para el próximo cliente de la misma especie.
  const handleSaveAndDuplicate = async () => {
    const ok = await save();
    if (!ok) return;
    setSavedCount(prev => prev + 1);
    setForm(prev => ({ ...prev, nroComitente: '', cliente: '', cuit: '', tipoDocumento: 'CUIT' }));
  };

  const muestraTasa = form.tramo === 'Competitivo' && especieSeleccionada?.modalidad === 'Tasa';
  const muestraPrecio = form.tramo === 'Competitivo' && especieSeleccionada?.modalidad === 'Precio';

  return (
    <Modal
      isOpen={isOpen}
      title={editingOrden ? 'Editar Orden' : `Nueva Orden${savedCount > 0 ? ` (${savedCount} guardadas)` : ''}`}
      maxWidth="760px"
      onSubmit={handleSubmit}
      error={formError}
      footer={<>
        <button type="button" onClick={onClose} style={{ ...fieldStyles.button, ...fieldStyles.secondaryButton }}>
          {savedCount > 0 ? 'Cerrar' : 'Cancelar'}
        </button>
        {!editingOrden && (
          <button type="button" onClick={handleSaveAndDuplicate} style={{ ...fieldStyles.button, backgroundColor: '#374151' }} disabled={isSaving}>
            Guardar y duplicar
          </button>
        )}
        <button type="submit" style={fieldStyles.button} disabled={isSaving}>
          {isSaving ? 'Guardando...' : 'Guardar Orden'}
        </button>
      </>}
    >
      <div className="axon-form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.85rem 1.5rem' }}>
        <div>
          <label style={fieldStyles.label}>Especie</label>
          <select name="especieId" value={form.especieId} onChange={handleEspecieChange} style={fieldStyles.select} disabled={!!editingOrden} required>
            <option value="" disabled>Seleccionar...</option>
            {especies.map(esp => (
              <option key={esp.id} value={esp.id}>
                {esp.denominacion}{esp.clase ? ` (${esp.clase})` : ''} — licita {esp.modalidad}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={fieldStyles.label}>Tramo</label>
          <select name="tramo" value={form.tramo} onChange={handleInputChange} style={fieldStyles.select}>
            {formOptions.tramoLicitacion.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>

        {conColocadores && (
          <div style={{ gridColumn: 'span 2' }}>
            <label style={fieldStyles.label} htmlFor="orden-colocador">Colocador</label>
            <select
              id="orden-colocador" name="colocadorId" value={form.colocadorId} onChange={handleInputChange}
              style={fieldStyles.select} disabled={colocadores.length === 1} required
            >
              <option value="" disabled>Seleccioná por cuál colocador va la orden...</option>
              {colocadores.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </div>
        )}

        <div>
          <label style={fieldStyles.label}>Nro Comitente</label>
          <Select
            name="nroComitente"
            options={comitenteOptions}
            value={comitenteOptions.find(c => c.value === form.nroComitente) || null}
            onChange={handleComitenteChange}
            placeholder="Buscar por Nro de Comitente..."
            isClearable
            menuPortalTarget={document.body}
            styles={{
              control: (base) => ({ ...base, ...fieldStyles.input, padding: '0.1rem', fontSize: '0.875rem' }),
              ...fieldStyles.reactSelect,
            }}
          />
        </div>
        <div>
          <label style={fieldStyles.label}>Cliente</label>
          <Select
            name="cliente"
            options={clienteOptions}
            value={clienteOptions.find(c => c.value === form.nroComitente) || null}
            onChange={handleComitenteChange}
            placeholder="Buscar por nombre de cliente..."
            isClearable
            menuPortalTarget={document.body}
            styles={{
              control: (base) => ({ ...base, ...fieldStyles.input, padding: '0.1rem', fontSize: '0.875rem' }),
              ...fieldStyles.reactSelect,
            }}
          />
        </div>

        <div>
          <label style={fieldStyles.label}>CUIT (se guarda para la próxima)</label>
          <input
            type="text" name="cuit" value={form.cuit} onChange={handleInputChange}
            onBlur={(e) => setForm(prev => ({ ...prev, cuit: formatCuitForDisplay(e.target.value) }))}
            style={fieldStyles.input} placeholder="Ej: 30-71882245-5"
          />
        </div>
        <div>
          <label style={fieldStyles.label}>Tipo de documento</label>
          <select name="tipoDocumento" value={form.tipoDocumento} onChange={handleInputChange} style={fieldStyles.select}>
            {formOptions.tipoDocumentoCliente.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>
        <div>
          <label style={fieldStyles.label}>Comercial</label>
          <select name="comercial" value={form.comercial} onChange={handleInputChange} style={fieldStyles.select} disabled={userRole === 'Comercial'}>
            <option value="">Seleccione un Comercial</option>
            {commercialOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>

        <div>
          <label style={fieldStyles.label}>Cantidad (VN)</label>
          <input
            type="text" inputMode="decimal" name="cantidadVN" value={vnInputValue}
            onChange={(e) => setVnInputValue(e.target.value)}
            onFocus={(e) => setVnInputValue(parseVnForState(e.target.value))}
            onBlur={(e) => setVnInputValue(formatNumberForDisplay(parseVnForState(e.target.value), 0))}
            style={fieldStyles.input} required
          />
        </div>
        <div>
          <label style={fieldStyles.label}>Moneda de integración</label>
          <select name="monedaIntegracion" value={form.monedaIntegracion} onChange={handleInputChange} style={fieldStyles.select} required>
            <option value="" disabled>Seleccionar...</option>
            {(especieSeleccionada?.monedasIntegracion || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>

        {muestraTasa && (
          <div>
            <label style={fieldStyles.label}>Tasa ofertada (%)</label>
            <input
              type="text" inputMode="decimal" name="tasaOfertada" value={tasaInputValue}
              onChange={(e) => setTasaInputValue(e.target.value)}
              onFocus={(e) => setTasaInputValue(formatNumberForInput(e.target.value))}
              onBlur={(e) => setTasaInputValue(formatTasaForDisplay(parseTasaForState(e.target.value)))}
              style={fieldStyles.input} required
            />
          </div>
        )}
        {muestraPrecio && (
          <div>
            <label style={fieldStyles.label}>Precio ofertado (c/1000)</label>
            <input
              type="text" inputMode="decimal" name="precioOfertado" value={precioInputValue}
              onChange={(e) => setPrecioInputValue(e.target.value)}
              onFocus={(e) => setPrecioInputValue(formatNumberForInput(e.target.value))}
              onBlur={(e) => setPrecioInputValue(formatNumberForDisplay(parseNumberForState(e.target.value)))}
              style={fieldStyles.input} required
            />
          </div>
        )}
        {form.tramo === 'No Competitivo' && (
          <div style={{ alignSelf: 'end', fontSize: '0.8rem', color: '#6B7281', paddingBottom: '0.6rem' }}>
            Tramo no competitivo: sin tasa/precio ofertado.
          </div>
        )}

        <div style={{ gridColumn: 'span 2' }}>
          <label style={fieldStyles.label}>Observaciones</label>
          <textarea name="obs" value={form.obs} onChange={handleInputChange} style={fieldStyles.textarea} />
        </div>
      </div>
    </Modal>
  );
};

export default LicitacionOrdenFormModal;
